import { useState, useEffect } from 'react';
import { getPeeths, markPeethViewed } from '../services/api';
import { triggerToast } from '../components/Toast';
import ImageWithFallback from '../components/ImageWithFallback';
import ShaktiPeethIllustration from '../components/PeethIllustration';
import BackButton from '../components/BackButton';
import { LockIcon } from '../components/SacredIcons';
import { useLang, localizePeeth, TOTAL_PEETHS } from '../i18n';

// ── Inline stylised icons (replace 🕉 / 🔱 emojis) ───────────────────────────
function OmIcon({ size = 22, color = 'var(--saffron-dark)' }) {
  // Render the actual Devanagari ॐ glyph in the Hindi serif font — looks
  // like a proper sacred symbol rather than the OS emoji.
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        fontFamily: "'Tiro Devanagari Hindi', 'Mukta', serif",
        fontSize: size,
        lineHeight: 1,
        color,
        fontWeight: 700,
        textShadow: '0 1px 0 rgba(255,180,90,0.20)',
        verticalAlign: 'middle',
      }}
    >ॐ</span>
  );
}

function TrishulIcon({ size = 18, color = 'var(--saffron-dark)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
      {/* central prong */}
      <path d="M16 4 L16 17" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
      <path d="M14 4 L16 1 L18 4" stroke={color} strokeWidth="2" strokeLinejoin="round" fill={color} />
      {/* side prongs */}
      <path d="M9 6 L9 16 C9 17.5 10 18 11 18" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" />
      <path d="M7 7 L9 4 L11 7" stroke={color} strokeWidth="2" strokeLinejoin="round" fill={color} />
      <path d="M23 6 L23 16 C23 17.5 22 18 21 18" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" />
      <path d="M21 7 L23 4 L25 7" stroke={color} strokeWidth="2" strokeLinejoin="round" fill={color} />
      {/* crossbar + staff */}
      <path d="M7 18 L25 18" stroke={color} strokeWidth="2.4" strokeLinecap="round" />
      <path d="M16 18 L16 30" stroke={color} strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

function formatRam(n) {
  if (n >= 100000) return (n / 100000).toFixed(1).replace(/\.0$/, '') + ' लाख';
  if (n >= 1000)   return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
}

function PeethModal({ peeth, onClose }) {
  const { t } = useLang();
  const locked = !peeth.is_unlocked;

  return (
    <div
      className="peeth-modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="peeth-modal" onClick={e => e.stopPropagation()}>
        {/* Hero image */}
        {peeth.image_url ? (
          <img
            src={peeth.image_url}
            alt={peeth.name}
            className="peeth-modal-hero"
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="illus-hero-wrap">
            <ShaktiPeethIllustration
              peethId={peeth.peeth_id}
              state={peeth.state}
              country={peeth.country}
            />
            <div className="illus-hero-gradient"
              style={{ background: 'linear-gradient(transparent, #FFF5E8)' }} />
          </div>
        )}

        <button className="peeth-modal-close" onClick={onClose} aria-label={t('common.close')}>✕</button>

        <div style={{ padding: '16px 20px 28px' }}>
          {/* Lock notice */}
          {locked && (
            <div style={{
              background: 'rgba(255,235,200,0.7)',
              border: '1px solid rgba(201,130,26,0.25)',
              borderRadius: 12, padding: '10px 16px',
              marginBottom: 16, textAlign: 'center',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
              <LockIcon size={16} color="var(--saffron-dark)" />
              <span style={{ fontFamily: 'var(--font-hindi)', fontSize: 14, fontWeight: 600, color: 'var(--text-dark)' }}>
                {t('peeths.unlockAt', { ram: formatRam(peeth.ram_count_to_unlock) })}
              </span>
            </div>
          )}

          <div className="peeth-modal-number">{t('peeths.modalNumber', { id: peeth.peeth_id })}</div>
          <div className="peeth-modal-name">{peeth.name}</div>
          {peeth.temple_name && (
            <div className="peeth-modal-temple">{peeth.temple_name}</div>
          )}

          {/* Info Grid */}
          <div className="peeth-info-grid" style={{ marginTop: 16, marginBottom: 16 }}>
            {[
              { label: t('info.location'),       value: peeth.location },
              { label: t('info.state'),          value: peeth.state },
              { label: t('info.country'),        value: peeth.country || 'India' },
              { label: t('peeths.infoBodyPart'), value: peeth.body_part },
              { label: t('peeths.infoShakti'),   value: peeth.shakti_name },
              { label: t('peeths.infoBhairava'), value: peeth.bhairava_name },
            ].filter(i => i.value).map(item => (
              <div key={item.label} className="peeth-info-item">
                <div className="peeth-info-label">{item.label}</div>
                <div className="peeth-info-value">{item.value}</div>
              </div>
            ))}
          </div>

          {/* History */}
          {peeth.history && (
            <div className="peeth-section">
              <div className="peeth-section-title">
                <OmIcon /> <span>{t('section.history')}</span>
              </div>
              <div className="peeth-section-text">{peeth.history}</div>
            </div>
          )}

          {/* Significance */}
          {peeth.significance && (
            <div className="peeth-section">
              <div className="peeth-section-title">
                <TrishulIcon /> <span>{t('section.significance')}</span>
              </div>
              <div className="peeth-section-text">{peeth.significance}</div>
            </div>
          )}

          {/* Ram count needed */}
          <div style={{
            textAlign: 'center',
            padding: '10px',
            marginTop: 8,
            background: 'rgba(255,255,255,0.03)',
            borderRadius: 10,
            border: '1px solid rgba(212,175,55,0.1)',
          }}>
            <span style={{ fontFamily: 'var(--font-hindi)', fontSize: 13, fontWeight: 600, color: 'var(--text-soft)', opacity: 0.9 }}>
              {peeth.is_unlocked
                ? t('peeths.ramUnlocked', { ram: formatRam(peeth.ram_count_to_unlock) })
                : t('peeths.ramWillUnlock', { ram: formatRam(peeth.ram_count_to_unlock) })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PeethsScreen({ user, isGuest, onBack }) {
  const { lang, t } = useLang();
  const [peeths, setPeeths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all'); // all | unlocked | locked

  useEffect(() => {
    async function load() {
      try {
        const data = await getPeeths();
        setPeeths(data.peeths || data || []);
      } catch {
        triggerToast(t('peeths.loadError'));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSelect(peeth) {
    // Locked peeths stay closed — reveal nothing until the Ram-count threshold
    // is reached. Give gentle feedback instead of opening the detail card.
    if (!peeth.is_unlocked) {
      triggerToast(t('peeths.unlockAt', { ram: formatRam(peeth.ram_count_to_unlock) }));
      return;
    }
    setSelected(peeth);
    if (!peeth.viewed && !isGuest) {
      try {
        await markPeethViewed(peeth.peeth_id);
        setPeeths(prev => prev.map(p =>
          p.peeth_id === peeth.peeth_id ? { ...p, viewed: true } : p
        ));
      } catch {
        // non-critical
      }
    }
  }

  const localized = peeths.map(p => localizePeeth(p, lang));
  const unlocked  = localized.filter(p => p.is_unlocked).length;
  const total     = localized.length || TOTAL_PEETHS;

  const filtered = filter === 'unlocked'
    ? localized.filter(p => p.is_unlocked)
    : filter === 'locked'
    ? localized.filter(p => !p.is_unlocked)
    : localized;

  return (
    <div className="screen" style={{ padding: 0, overflow: 'hidden', height: '100dvh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="screen-header">
        <BackButton onClick={onBack} />
        <div style={{ flex: 1 }}>
          <div className="screen-header-title">{t('peeths.title', { total })}</div>
          <div className="screen-header-sub">{t('peeths.unlockedCount', { n: unlocked, total })}</div>
        </div>
        <div style={{
          background: 'rgba(230,81,0,0.12)',
          border: '1px solid rgba(201,130,26,0.30)',
          borderRadius: 10,
          padding: '4px 10px',
          fontFamily: 'var(--font-display)',
          fontSize: 12,
          color: 'var(--saffron)',
        }}>
          {Math.round((unlocked / total) * 100)}%
        </div>
      </div>

      {/* Progress Bar */}
      <div className="progress-bar-wrap">
        <div className="progress-bar-bg">
          <div className="progress-bar-fill" style={{ width: `${(unlocked / total) * 100}%` }} />
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="filter-tabs-wrap">
        {[
          { id: 'all',      label: t('peeths.filterAll') },
          { id: 'unlocked', label: t('peeths.filterUnlocked', { n: unlocked }) },
          { id: 'locked',   label: t('peeths.filterLocked', { n: total - unlocked }) },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            style={{
              padding: '6px 14px',
              borderRadius: 20,
              border: filter === tab.id
                ? '1.5px solid rgba(201,130,26,0.55)'
                : '1px solid rgba(201,130,26,0.18)',
              background: filter === tab.id
                ? 'linear-gradient(135deg, rgba(230,81,0,0.18), rgba(201,130,26,0.25))'
                : 'rgba(255,248,232,0.5)',
              color: filter === tab.id ? 'var(--saffron-dark)' : 'var(--text-soft)',
              fontFamily: 'var(--font-hindi)',
              fontSize: 13,
              cursor: 'pointer',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        // Mirror the real .peeth-grid + .peeth-card shape so the skeleton reads
        // as the actual cards loading in (same columns, gap, aspect, radius).
        <div className="peeth-grid" style={{ flex: 1 }}>
          {Array.from({ length: 12 }, (_, i) => (
            <div key={i} className="skeleton" style={{ aspectRatio: '3/4', minHeight: 200, borderRadius: 16 }} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
          <OmIcon size={48} color="var(--gold)" />
          <p style={{ fontFamily: 'var(--font-hindi)', fontSize: 18, color: 'var(--cream)', opacity: 0.6 }}>
            {filter === 'unlocked' ? t('peeths.emptyUnlocked') : t('peeths.emptyAll')}
          </p>
        </div>
      ) : (
        <div className="peeth-grid" style={{ flex: 1 }}>
          {filtered.map((peeth, idx) => (
            <div
              key={peeth.peeth_id}
              className={`peeth-card ${peeth.is_unlocked ? 'peeth-card-unlocked' : 'peeth-card-locked'}`}
              style={{ '--i': idx }}
              onClick={() => handleSelect(peeth)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && handleSelect(peeth)}
            >
              {peeth.image_url ? (
                <ImageWithFallback
                  src={peeth.image_url}
                  alt={peeth.name}
                  className="peeth-card-img"
                  fallbackGradient="linear-gradient(135deg, #5C0010, #800000)"
                />
              ) : (
                <div className="peeth-card-img" style={{ overflow:'hidden', borderRadius:'inherit' }}>
                  <ShaktiPeethIllustration
                    peethId={peeth.peeth_id}
                    state={peeth.state}
                    country={peeth.country}
                  />
                </div>
              )}
              <div className="peeth-card-overlay">
                <div className="peeth-card-number">#{peeth.peeth_id}</div>
                <div className="peeth-card-name">{peeth.name}</div>
                <div className="peeth-card-location">{peeth.state}</div>
              </div>
              {!peeth.is_unlocked && (
                <div className="peeth-lock-badge">
                  <LockIcon size={13} style={{ marginRight: 4 }} />{t('peeths.lockBadge', { ram: formatRam(peeth.ram_count_to_unlock) })}
                </div>
              )}
              {peeth.is_unlocked && !peeth.viewed && (
                <div style={{
                  position: 'absolute', top: 8, right: 8,
                  width: 10, height: 10, borderRadius: '50%',
                  background: 'var(--saffron)',
                  boxShadow: '0 0 6px rgba(255,107,0,0.7)',
                }} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Peeth Detail Modal */}
      {selected && (
        <PeethModal peeth={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
